import {
  COCO_WS_AUTO_CONTINUE,
  COCO_WS_FAST_WAKE,
  COCO_WS_REQUIRE_WAKE,
  COCO_WS_SILENCE_DURATION,
  COCO_WS_SKIP_WAKE_TTS,
  COCO_WS_WITH_TTS,
} from '../config/cocoVoice';
import {
  buildCocoListenWsUrlCandidates,
  getCocoSessionId,
  normalizeVoiceResponse,
  setCocoSessionId,
} from '../services/cocoVoiceApi';
import { createPcmCapture } from './cocoVoicePcmCapture';
import { matchesWakePhrase } from './cocoWakeLocal';

const RECONNECT_MS = 3000;
const WAKE_DEBOUNCE_MS = 2500;
const CONNECT_TIMEOUT_MS = 8000;

function isServerWakeAck(data) {
  if (!data) return false;
  if (data.awake === true) return true;
  if (data.skip_wake_tts) return true;
  const text = String(data.response_text || '');
  return /我在呢|已唤醒|请说/.test(text) && !data.transcript;
}

export function createCocoVoiceWsListener({
  onState,
  onResponse,
  onHint,
  onError,
  onWake,
  requireWake = COCO_WS_REQUIRE_WAKE,
  withTts = COCO_WS_WITH_TTS,
  autoContinue = COCO_WS_AUTO_CONTINUE,
  silenceDuration = COCO_WS_SILENCE_DURATION,
  fastWake = COCO_WS_FAST_WAKE,
  skipWakeTts = COCO_WS_SKIP_WAKE_TTS,
} = {}) {
  let ws = null;
  let pcm = null;
  let paused = false;
  let mutedForTts = false;
  let reconnectTimer = null;
  let stopped = false;
  let lastWakeAt = 0;
  let urlCandidates = [];
  let opened = false;
  let closingIntentionally = false;
  let pendingSockets = [];
  let connectAttemptId = 0;
  let connectTimeoutTimer = null;

  const closeWsSilently = (socket) => {
    if (!socket) return;
    if (socket.readyState === WebSocket.CONNECTING) {
      socket.onopen = () => {
        try { socket.close(); } catch { /* ignore */ }
      };
      return;
    }
    try { socket.close(); } catch { /* ignore */ }
  };

  const triggerWake = (payload) => {
    const now = Date.now();
    if (now - lastWakeAt < WAKE_DEBOUNCE_MS) return;
    lastWakeAt = now;
    onWake?.(payload);
  };

  const clearReconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const sendConfig = () => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({
      type: 'config',
      session_id: getCocoSessionId(),
      require_wake: requireWake,
      with_tts: withTts,
      auto_continue: autoContinue,
      silence_duration: silenceDuration,
      fast_wake: fastWake,
      skip_wake_tts: skipWakeTts,
    }));
  };

  const sendWake = () => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify({ type: 'wake', session_id: getCocoSessionId() }));
    return true;
  };

  const handleJsonMessage = async (msg) => {
    const type = msg?.type;
    if (msg?.session_id) setCocoSessionId(msg.session_id);

    if (type === 'ready') {
      onState?.('ready');
      return;
    }
    if (type === 'listening') {
      onState?.('listening');
      return;
    }
    if (type === 'speech') {
      onState?.('speech');
      return;
    }
    if (type === 'processing') {
      onState?.('processing');
      return;
    }
    if (type === 'transcript') {
      const text = msg.transcript || msg.text || '';
      if (text) onHint?.(`听到：${text}`);
      if (matchesWakePhrase(text)) {
        triggerWake({ source: 'transcript', text });
      }
      onState?.('transcript');
      return;
    }
    if (type === 'response' || msg.response_text || msg.audio_base64) {
      const data = normalizeVoiceResponse(msg);
      if (isServerWakeAck(data)) {
        triggerWake({ source: 'server', text: data.response_text || data.transcript });
      }
      if (!data.awake && !data.response_text && data.hint) {
        onHint?.(data.hint);
        onState?.('listening');
        return;
      }
      onResponse?.(data);
      onState?.('response');
      return;
    }
    if (type === 'error') {
      onError?.(new Error(msg.message || msg.detail || '语音服务错误'));
      return;
    }
    if (msg.hint) {
      onHint?.(msg.hint);
    }
  };

  const clearConnectTimeout = () => {
    if (connectTimeoutTimer) {
      clearTimeout(connectTimeoutTimer);
      connectTimeoutTimer = null;
    }
  };

  const closeAllSockets = () => {
    clearConnectTimeout();
    connectAttemptId += 1;
    pendingSockets.forEach((socket) => closeWsSilently(socket));
    pendingSockets = [];
    closeWsSilently(ws);
    ws = null;
    opened = false;
  };

  const attachActiveSocket = (socket) => {
    socket.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      try {
        const msg = JSON.parse(event.data);
        handleJsonMessage(msg);
      } catch (e) {
        onError?.(new Error(e?.message || '语音消息解析失败'));
      }
    };

    socket.onclose = () => {
      if (ws !== socket) return;
      ws = null;
      opened = false;
      if (closingIntentionally || stopped || paused) return;
      onState?.('reconnecting');
      clearReconnect();
      reconnectTimer = setTimeout(connect, RECONNECT_MS);
    };
  };

  const connect = () => {
    if (stopped || paused) return;
    clearReconnect();
    if (!urlCandidates.length) urlCandidates = buildCocoListenWsUrlCandidates();

    closeAllSockets();
    const attemptId = connectAttemptId;
    onState?.('connecting');

    let failedCount = 0;
    const candidates = [...new Set(urlCandidates.filter(Boolean))];
    if (!candidates.length) {
      onError?.(new Error('未配置语音 WebSocket 地址'));
      return;
    }

    const onAllFailed = () => {
      if (attemptId !== connectAttemptId || opened || stopped || paused || closingIntentionally) return;
      onError?.(new Error('语音 WebSocket 连接异常，请检查网络或稍后重试'));
      onState?.('reconnecting');
      clearReconnect();
      reconnectTimer = setTimeout(connect, RECONNECT_MS);
    };

    connectTimeoutTimer = setTimeout(onAllFailed, CONNECT_TIMEOUT_MS);

    candidates.forEach((url) => {
      const socket = new WebSocket(url);
      socket.binaryType = 'arraybuffer';
      pendingSockets.push(socket);

      const markFailed = () => {
        if (attemptId !== connectAttemptId || opened) return;
        failedCount += 1;
        pendingSockets = pendingSockets.filter((item) => item !== socket);
        if (failedCount >= candidates.length) {
          clearConnectTimeout();
          onAllFailed();
        }
      };

      socket.onopen = () => {
        if (attemptId !== connectAttemptId || stopped || paused || opened) {
          closeWsSilently(socket);
          pendingSockets = pendingSockets.filter((item) => item !== socket);
          return;
        }
        clearConnectTimeout();
        opened = true;
        ws = socket;
        pendingSockets.forEach((item) => {
          if (item !== socket) closeWsSilently(item);
        });
        pendingSockets = [];
        attachActiveSocket(socket);
        sendConfig();
        onState?.('listening');
      };

      socket.onerror = () => {
        // 等 onclose 汇总；浏览器控制台仍会打印失败日志，属正常现象
      };

      socket.onclose = () => {
        if (ws === socket) return;
        markFailed();
      };
    });
  };

  const startMic = async () => {
    if (pcm?.isRunning()) return;
    pcm = await createPcmCapture({
      onChunk: (chunk) => {
        if (mutedForTts || paused || !ws || ws.readyState !== WebSocket.OPEN) return;
        ws.send(chunk.buffer);
      },
      onError,
    });
    await pcm.start();
  };

  return {
    async start() {
      stopped = false;
      paused = false;
      closingIntentionally = false;
      urlCandidates = buildCocoListenWsUrlCandidates();
      connect();
      await startMic();
      if (stopped || paused) return;
    },

    sendWake,

    pause() {
      paused = true;
      clearReconnect();
      pcm?.setMuted(true);
      closingIntentionally = true;
      closeAllSockets();
      closingIntentionally = false;
      onState?.('paused');
    },

    async resume() {
      paused = false;
      mutedForTts = false;
      pcm?.setMuted(false);
      if (!pcm?.isRunning()) {
        await startMic();
      }
      connect();
      onState?.('listening');
    },

    setMutedForTts(muted) {
      mutedForTts = muted;
      pcm?.setMuted(muted || paused);
    },

    async stop() {
      stopped = true;
      paused = false;
      clearReconnect();
      closingIntentionally = true;
      closeAllSockets();
      closingIntentionally = false;
      await pcm?.stop();
      pcm = null;
      urlCandidates = [];
    },

    isPaused() {
      return paused;
    },
  };
}
